/**
 * 보호 라우트 - 미인증 시 로그인, store_id 미설정 시 지점선택으로 리다이렉트
 *
 * `minRole`(선택, dev-steps `2026-07-07-zoolungman-coin-grant-app-dev-steps.md` §S5 작업 3):
 *   지정하면 해당 역할 미만 사용자를 메뉴(`/`)로 되돌립니다. **미전달이면 아래 가드를 통째로
 *   건너뛰므로 `minRole` 을 넘기지 않은 기존 라우트는 동작이 전혀 바뀌지 않습니다.**
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { isAtLeast } from '../utils/roleUtils';

export default function ProtectedRoute({ children, allowWithoutStore = false, minRole }) {
  const { user, loading } = useAuth();
  // 🔴 요청한 경로를 리다이렉트에 실어 보낸다 (2026-08-06).
  //    예전에는 그냥 `/login` 으로 보내고 로그인 후 항상 `/` 로 갔다. 그래서 폰에서
  //    `/zoolungman` 링크를 열면 로그인 뒤 메뉴로 떨어져 다시 찾아 들어가야 했다.
  //    `LoginPage`·`StoreSelectPage` 가 이 값을 읽어 원래 자리로 되돌린다 — 셋은 한 묶음이다.
  const location = useLocation();

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '1.2rem',
        color: '#666'
      }}>
        로딩 중...
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // 역할 게이팅(선택) — `minRole` 을 넘긴 라우트에만 적용된다. 미전달이면 단락 평가로 통과하므로
  // 기존 라우트는 무영향. 인증(`!user`) 직후에 두어 "인증 → 인가" 순서를 지킨다.
  // ⚠️ UX 게이트일 뿐이며 최종 방어는 서버 `require_role`(예: `POST /tools/coin/give` = STAFF).
  if (minRole && !isAtLeast(user.role, minRole)) {
    return <Navigate to="/" replace />;
  }

  // store_id 미설정 시 지점 선택 페이지로 리다이렉트
  // JWT에 store_id가 있으면 (계정에 매장 귀속) localStorage에도 동기화
  if (user.store_id) {
    localStorage.setItem('store_id', user.store_id);
    // store_name 은 빈값으로 덮어쓰지 않는다. login 응답엔 store_name 이 없을 수 있어
    // (/auth/me 에만 포함) '' 로 클로버하면 StoreBadge 가 가려진다 → 기존값 보존.
    if (user.store_name) {
      localStorage.setItem('store_name', user.store_name);
    }
  }
  if (!allowWithoutStore && !user.store_id && !localStorage.getItem('store_id')) {
    // 지점선택을 거치는 경우에도 목적지를 잃지 않는다 — 로그인 직후 지점이 없으면
    // `/login → 목적지 → /store-select` 순으로 오므로 여기서도 실어 보내야 한다.
    return <Navigate to="/store-select" state={{ from: location }} replace />;
  }

  return children;
}
